import { useCallback } from 'react';

import { useUIDSeed } from '@twilio-paste/core/dist/uid-library';
import { FormControl, FormSection, FormSectionHeading } from '@twilio-paste/core/form';
import { HelpText } from '@twilio-paste/core/help-text';
import { Input } from '@twilio-paste/core/input';
import { Label } from '@twilio-paste/core/label';
import { Radio, RadioGroup } from '@twilio-paste/core/radio-group';
import { Option, Select } from '@twilio-paste/core/select';
import { Field, useFormikContext } from 'formik';

import { useTwilioAccountsList } from '../../../modules/TwilioAccounts/twilio-accounts.api';

import { AppSettingsFormValues, TwilioAccountOption } from './AppSettingsForm';

interface TwilioAccountSectionProps {
  readOnly?: boolean;
}

export function TwilioAccountSection({ readOnly }: TwilioAccountSectionProps) {
  const seed = useUIDSeed();
  const {
    touched,
    errors,
    values: { twilioAccountOption, twilioAccountId },
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<AppSettingsFormValues>();

  const { data: twilioAccounts = [] } = useTwilioAccountsList();

  const handleSelectTwilioOption = useCallback(
    (option: string) => setFieldValue('twilioAccountOption', option),
    [setFieldValue],
  );

  const handleSelectTwilioAccount = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setFieldValue('twilioAccountId', event.target.value);
      setFieldTouched('twilioAccountId', true);
    },
    [setFieldTouched, setFieldValue],
  );

  return (
    <FormSection>
      <FormSectionHeading>Twilio Account Connection</FormSectionHeading>
      <RadioGroup
        orientation="horizontal"
        value={twilioAccountOption}
        name="Twilio Account"
        legend=""
        onChange={handleSelectTwilioOption}
        disabled={readOnly}
      >
        <Radio value={TwilioAccountOption.Existing} readOnly={readOnly}>
          Existing Account
        </Radio>
        <Radio value={TwilioAccountOption.New} readOnly={readOnly}>
          New Account
        </Radio>
      </RadioGroup>
      {twilioAccountOption === TwilioAccountOption.New ? (
        <>
          <FormControl>
            <Label htmlFor={seed('twilioAccountSid')} required>
              Twilio Account SID
            </Label>
            <Field
              name="twilioAccountSid"
              as={Input}
              type="text"
              id={seed('twilioAccountSid')}
              placeholder="Twilio Account SID"
              hasError={touched.twilioAccountSid && !!errors.twilioAccountSid}
              readOnly={readOnly}
            />
            {touched.twilioAccountSid && !!errors.twilioAccountSid && (
              <HelpText variant="error">{errors.twilioAccountSid}</HelpText>
            )}
          </FormControl>
          <FormControl>
            <Label htmlFor={seed('twilioAccountAuthToken')} required>
              Twilio Account Auth Token
            </Label>
            <Field
              name="twilioAccountAuthToken"
              as={Input}
              type="password"
              id={seed('twilioAccountAuthToken')}
              placeholder="Twilio Account Auth Token"
              hasError={touched.twilioAccountAuthToken && !!errors.twilioAccountAuthToken}
              readOnly={readOnly}
            />
            {touched.twilioAccountAuthToken && !!errors.twilioAccountAuthToken && (
              <HelpText variant="error">{errors.twilioAccountAuthToken}</HelpText>
            )}
          </FormControl>
        </>
      ) : (
        <>
          <Label htmlFor={seed('twilioAccountId')} required>
            Select Twilio Account
          </Label>
          <Select value={twilioAccountId} onChange={handleSelectTwilioAccount} disabled={readOnly}>
            <Option value=""> </Option>
            {twilioAccounts.map(({ sid, friendlyName, twilioAccountId }) => (
              <Option
                key={twilioAccountId}
                value={twilioAccountId}
              >{`${sid} - ${friendlyName}`}</Option>
            ))}
          </Select>
          {touched.twilioAccountId && !!errors.twilioAccountId && (
            <HelpText variant="error">{errors.twilioAccountId}</HelpText>
          )}
        </>
      )}
    </FormSection>
  );
}
